import { isEqual } from "lodash";
import { useCallback, useReducer } from "react";
import i18n from "../../services/localization-service/i18n";

const currentDate = new Date();

export const ALL_TANKS_OPTION_ID = -1;
export const allTanksOption = { id: ALL_TANKS_OPTION_ID, displayText: i18n.t("runTicketFilter.allTanks") };

export type TankOption = {
    id: number;
    displayText: string;
};

type RunTicketListStateType = {
    // Options state
    timestamp: Date;
    tanks: Array<TankOption>;
    downloadUid: string | undefined;

    // Other state
    isOptionsDirty: boolean;
};

export const initialState: RunTicketListStateType = {
    timestamp: new Date(currentDate.getFullYear(), currentDate.getMonth(), 1),
    tanks: [allTanksOption],
    downloadUid: undefined,
    isOptionsDirty: false
};

enum RunTicketListActionEnum {
    ResetOptionsRequested = "RESET_OPTIONS_REQUESTED",
    TanksChanged = "TANKS_CHANGED",
    TimestampChanged = "TIMESTAMP_CHANGED",
    DownloadUidChanged = "DOWNLOAD_UID_CHANGED"
}

type RunTicketListActionTypes = {
    type: RunTicketListActionEnum.ResetOptionsRequested;
} | {
    type: RunTicketListActionEnum.TanksChanged;
    tanks: Array<TankOption>;
} | {
    type: RunTicketListActionEnum.TimestampChanged;
    timestamp: Date;
} | {
    type: RunTicketListActionEnum.DownloadUidChanged;
    payload: string | undefined;
};

function reducer(state: RunTicketListStateType, action: RunTicketListActionTypes): RunTicketListStateType {

    const copy = { ...state };
    switch (action.type) {

        case RunTicketListActionEnum.ResetOptionsRequested:
            copy.tanks = initialState.tanks;
            copy.timestamp = initialState.timestamp;
            copy.isOptionsDirty = false;
            return copy;

        case RunTicketListActionEnum.TanksChanged:
            copy.tanks = action.tanks;
            copy.isOptionsDirty = isOptionsDirty(copy);
            return copy;

        case RunTicketListActionEnum.TimestampChanged:
            copy.timestamp = action.timestamp;
            copy.isOptionsDirty = isOptionsDirty(copy);
            return copy;

        case RunTicketListActionEnum.DownloadUidChanged:
            copy.downloadUid = action.payload;
            return copy;
    }
}

function isOptionsDirty(state: RunTicketListStateType) {
    return !isEqual({
        tanks: state.tanks,
        timestamp: state.timestamp
    }, {
        tanks: initialState.tanks,
        timestamp: initialState.timestamp
    });
}

type UseRunTicketListStateType = {
    state: RunTicketListStateType;
    resetOptions: () => void;
    setTanks: (tanks: Array<TankOption>) => void;
    setTimestamp: (timestamp: Date) => void;
    setDownloadUid: (value: string | undefined) => void;
};

export default function useRunTicketListState(): UseRunTicketListStateType {
    const [state, dispatch] = useReducer(reducer, initialState);

    const resetOptions = useCallback(() => dispatch({
            type: RunTicketListActionEnum.ResetOptionsRequested
        }), []);

    const setTanks = useCallback((tanks: Array<TankOption>) => dispatch({
            type: RunTicketListActionEnum.TanksChanged,
            tanks: tanks
        }), []);

    const setTimestamp = useCallback((timestamp: Date) => dispatch({
            type: RunTicketListActionEnum.TimestampChanged,
            timestamp: timestamp
        }), []);

    const setDownloadUid = useCallback((value: string | undefined) => dispatch({
            type: RunTicketListActionEnum.DownloadUidChanged,
            payload: value
        }), []);

    return {
        state,
        resetOptions,
        setTanks,
        setTimestamp,
        setDownloadUid
    };
}    
